"use client";

import React from "react";
import { Badge } from "@/components/ui/badge";
import { SmartUserDisplay, SmartUserChip } from "@/components/shared/smart-display/SmartUserDisplay";
import { SmartContactDisplay, SmartContactChip } from "@/components/shared/smart-display/SmartContactDisplay";
import { cn } from "@/lib/utils";
import { getStatusBadgeVariant } from "./utils";
import { formatDate } from "@/lib/utils/crm/time-entry-helpers";

// ============================================================================
// TYPES - Imported from shared types
// ============================================================================

import type {
  FieldValue,
  BaseEntity,
  SimpleFieldConfig,
  FieldRendererProps,
  FieldRendererFunction,
} from "./types";

// ============================================================================
// FIELD RENDERER BRIDGE - REMOVED
// ============================================================================
// The createSimpleTableFieldRenderer function has been removed in favor of
// the shared renderCellValue function in utils/cell-value-rendering.tsx to eliminate
// duplication and ensure consistency between desktop and mobile rendering.
//
// Use: import { renderCellValue } from './utils';

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const DueDateDisplay: React.FC<{ date: string | Date }> = ({ date }) => {
  if (!date) return <span className="text-gray-400">-</span>;

  // Parse date as local date to prevent timezone offset issues
  let dueDate: Date;
  if (typeof date === "string") {
    // Extract just the date part (YYYY-MM-DD) from various formats
    const dateMatch = date.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const [, yearStr, monthStr, dayStr] = dateMatch;
      const year = parseInt(yearStr, 10);
      const month = parseInt(monthStr, 10);
      const day = parseInt(dayStr, 10);
      dueDate = new Date(year, month - 1, day);
    } else {
      dueDate = new Date(date);
    }
  } else {
    dueDate = date;
  }

  const now = new Date();
  const diffDays = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  let variant: "default" | "destructive" | "secondary" = "default";
  let className = "";

  if (diffDays < 0) {
    variant = "destructive";
    className = "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200";
  } else if (diffDays === 0) {
    variant = "destructive";
    className = "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200";
  } else if (diffDays <= 3) {
    variant = "secondary";
    className = "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200";
  } else {
    variant = "default";
    className = "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200";
  }

  return (
    <Badge variant={variant} className={cn("text-xs", className)}>
      {formatDate(date as string)}
    </Badge>
  );
};

const NumberDisplay: React.FC<{ value: number }> = ({ value }) => {
  if (typeof value !== "number" || isNaN(value)) return <span className="text-gray-400">-</span>;

  // Check if it looks like currency (has decimal places or is large)
  if (value % 1 !== 0 || value > 1000) {
    return (
      <span className="text-card-foreground font-mono">
        {value.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        })}
      </span>
    );
  }

  return <span className="text-card-foreground font-mono">{value.toLocaleString()}</span>;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// ============================================================================
// CATEGORY AND PRIORITY HELPERS
// ============================================================================

export const getCategoryForField = (key: string, field: SimpleFieldConfig) => {
  if (key.includes("status") || key.includes("priority")) return "status";
  if (key.includes("user") || key.includes("assign")) return "assignment";
  if (key.includes("date") || key.includes("time") || key.includes("due")) return "dates";
  if (key.includes("email") || key.includes("phone") || key.includes("contact")) return "contact";
  if (key.includes("amount") || key.includes("cost") || key.includes("price")) return "financial";
  if (key.includes("tag") || key.includes("category") || key.includes("label")) return "metadata";
  return "basic";
};

export const getPriorityForField = (key: string, field: SimpleFieldConfig) => {
  // Primary fields (most important)
  if (key.includes("title") || key.includes("name")) return 1;
  if (key.includes("status")) return 2;
  if (key.includes("due_date") || key.includes("deadline")) return 3;

  // Secondary fields
  if (key.includes("assign") || key.includes("user")) return 10;
  if (key.includes("priority")) return 11;
  if (key.includes("tag")) return 12;

  // Tertiary fields
  return 20;
};

export const getMobilePriorityForField = (key: string, field: SimpleFieldConfig) => {
  // Mobile-specific priority (similar to desktop but optimized for mobile)
  return getPriorityForField(key, field);
};

export const getMobileTypeForField = (field: SimpleFieldConfig) => {
  switch (field.type) {
    case "select":
    case "boolean":
      return "badge" as const;
    case "user":
    case "multi-user":
    case "tags":
    case "client-select":
      return "secondary" as const;
    case "text":
    case "textarea":
    default:
      return "primary" as const;
  }
};

export { DueDateDisplay };
